/**
 * MessageTimestamp — italic timestamp line shown below a chat bubble.
 *
 * Flutter source of truth:
 *  - Own variant:  `currentUserMessageTime` (`chat_screen.dart:2295-2329`).
 *  - Peer variant: `peerUserMessageTime`    (`chat_screen.dart:2378-2416`).
 *
 * Both variants are structurally identical — same italic font, same
 * `AppColors.disabledColor`, same 12px font size, same horizontal padding (24
 * on the side that hugs the bubble). The only differences are:
 *  - Side alignment: own variant pads on the right
 *    (`EdgeInsets.only(right: 24)` at `chat_screen.dart:2302`); peer variant
 *    pads on the left (`EdgeInsets.only(left: 34)` at
 *    `chat_screen.dart:2385`).
 *  - Visibility rule: on the last-of-run bubble Flutter always renders the
 *    timestamp; on mid-run bubbles it renders only when the per-message
 *    `_messageTimeSeen[timestamp]` flag is true
 *    (`chat_screen.dart:2300-2329` / `:2383-2416`).
 *
 * Web port:
 *  - The "last-of-run" boolean is resolved by `MessageBubble`
 *    (`isLastMessageRight` for own, `isLastMessageLeft` for peer) and passed
 *    in as `isLastOfRun`.
 *  - The per-message expanded flag is resolved by the chat hook
 *    (`useChat`, Task 16) and passed in as `isTimestampOpen` — the hook owns
 *    the "only one open at a time" invariant globally (matches Flutter's
 *    `_messageTimeSeen.forEach` reset at `chat_screen.dart:1405-1409`).
 *  - When both flags are false we render `null` (Flutter's `Visibility` with
 *    `visible: false` returns a zero-size widget — we drop the row entirely).
 *
 * Date formatting:
 *  - Flutter calls `DateUtils.getChatDate(timestamp)` (`chat_screen.dart:2296`
 *    / `:2379`) which boils down to `getDate(timestamp)`
 *    (`lib/presentation/utils/date_utils.dart:74-76`).
 *  - The web equivalent is the locale-aware `useDateFormatters().formatDate`
 *    (`presentation/utils/useDateFormatters.ts:35-64`) — that helper already
 *    ports Flutter's `getDate` line-for-line (today → time, yesterday/tomorrow
 *    labels, dd MMM, dd MMM yyyy). Reusing it gives us l10n-aware month
 *    abbreviations and "Yesterday/Tomorrow" labels for free, matching the
 *    project's existing date-formatting pattern (Comments, Notifications,
 *    Wallet all go through it).
 *
 * Theme / responsive tokens:
 *  - Text color → `theme.disabled` (Flutter `AppColors.disabledColor`).
 *  - Font size  → `responsive.font.sm` (= 12px, exact Flutter parity at
 *    `chat_screen.dart:2308` / `:2391`).
 *  - Side padding → `responsive.spacing.lg` on the bubble side (Flutter uses
 *    24 on own / 24 on peer; `lg` is 16 — the closest available token).
 *    Documented as a small UI deviation per `presentation.md`.
 *  - Bottom margin → `responsive.spacing.xs` (Flutter `bottom: 5.0` at
 *    `chat_screen.dart:2303` / `:2386`; `xs` = 4, the closest token).
 *
 * No interactivity here — taps on the bubble itself fire
 * `onToggleTimestamp` and re-render this component with the new flag.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';

export interface MessageTimestampProps {
  /** Message timestamp (epoch ms). */
  timestamp: number;
  /** True iff the message is owned by the current user (right-aligned). */
  isOwn: boolean;
  /**
   * Whether this bubble is the run-end of its sender — Flutter's
   * `isLastMessageRight` (own) / `isLastMessageLeft` (peer). Last-of-run
   * always shows the timestamp; mid-run only shows when `isTimestampOpen`.
   */
  isLastOfRun: boolean;
  /**
   * Whether this specific message's timestamp is currently expanded by the
   * user (tap-to-toggle). Resolved by the chat hook so the "only one open
   * at a time" invariant is preserved globally.
   */
  isTimestampOpen: boolean;
}

export const MessageTimestamp: React.FC<MessageTimestampProps> = ({
  timestamp,
  isOwn,
  isLastOfRun,
  isTimestampOpen,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { formatDate } = useDateFormatters();

  // Mid-run + not-expanded → render nothing (Flutter's `Visibility(visible:
  // false)` collapses to a zero-size widget at `chat_screen.dart:2315-2329`
  // / `:2398-2415`).
  if (!isLastOfRun && !isTimestampOpen) {
    return null;
  }

  return (
    <div
      style={{
        // Side padding hugs the bubble side — own bubbles right-align, peer
        // bubbles left-align (Flutter `EdgeInsets.only(right: 24)` vs
        // `EdgeInsets.only(left: 34)`).
        paddingRight: isOwn ? responsive.spacing.lg : 0,
        paddingLeft: isOwn ? 0 : responsive.spacing.lg,
        marginBottom: responsive.spacing.xs,
        textAlign: isOwn ? 'right' : 'left',
        color: theme.disabled,
        fontSize: responsive.font.sm,
        fontStyle: 'italic',
      }}
    >
      {formatDate(timestamp)}
    </div>
  );
};
